import { HttpClient } from "@angular/common/http";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformServer } from "@angular/common";
import { NavigationExtras, Router } from "@angular/router";
import { BehaviorSubject, Observable, lastValueFrom, of } from "rxjs";
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { tap } from 'rxjs/operators';
import { environment } from "src/environments/environment";
import { LocalStorageService, STORAGE_CONSTANTS } from "./storage.service";
@Injectable({
  providedIn: 'root',
})
export class AppService {
  private static CHAT_ROUTE = 'chat';
  private static DASHBOARD_ROUTE = 'dashboard';
  private static COLLECTION_ROUTE = 'collections';
  baseUrl = `${environment.baseUrl}/v1`;
  chatBaseUrl: string;
  dashboardBaseUrl: string;
  collectionBaseUrl: string;
  
  private pageTitle$ = new BehaviorSubject<string|null>(null);
  private appConfig$ = Promise.resolve(null);
  private readonly isServer: boolean;

  constructor(
    private http: HttpClient,
    private router: Router,
    private storageService: LocalStorageService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isServer = isPlatformServer(platformId);
    this.chatBaseUrl = this.deriveFunctionUri(environment['chatBaseUrl'], AppService.CHAT_ROUTE);
    this.dashboardBaseUrl = this.deriveFunctionUri(environment['dashboardBaseUrl'], `${AppService.DASHBOARD_ROUTE}`);
    this.collectionBaseUrl = this.deriveFunctionUri(environment['collectionBaseUrl'], `${AppService.COLLECTION_ROUTE}`);
  }

  deriveFunctionUri(funcBaseUrl: string, routeName: string){
    if(!funcBaseUrl || funcBaseUrl.length<1) {
      return this.baseUrl.replace('/api/', `/${routeName}/api/`);
    }
    return funcBaseUrl;
  }

  getAppConfig(params?: any): Promise<any> {
    if(!this.appConfig$){
      this.appConfig$ = lastValueFrom(this.http.get(`${this.getAppBaseUrl()}/app-config`, { params: params })).then((resp:any) => {
        return resp;
      });
    }
    return this.appConfig$;
  }

  setTitle(title: string) {
    this.pageTitle$.next(title);
  }

  getTitle(): Observable<string|null>  {
    return this.pageTitle$.asObservable();
  }

  getAppBaseUrl(): string  {
    if (this.isServer && environment['ssrBaseUrl']) {
      return `${environment['ssrBaseUrl']}/v1`;
    }
    if (!this.isServer && typeof window !== 'undefined'
      && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
      return '/api/v1';
    }
    const fromStorage = this.storageService.get(STORAGE_CONSTANTS.APP_BASE_URL)
    return fromStorage ? `${fromStorage}/v1` : this.baseUrl;
  }

  getAppChatBaseUrl(): string  {
    const fromStorage = this.storageService.get(STORAGE_CONSTANTS.APP_CHAT_BASE_URL)
    return fromStorage ? `${fromStorage}/v1` : `${this.chatBaseUrl}`;
  }

  getDashboardBaseUrl(): string  {
    const fromStorage = this.storageService.get(STORAGE_CONSTANTS.APP_DASHBOARD_BASE_URL)
    return fromStorage ? `${fromStorage}` : `${this.dashboardBaseUrl}`;
  }

  getCollectionBaseUrl(): string  {
    const fromStorage = this.storageService.get(STORAGE_CONSTANTS.APP_COLLECTION_BASE_URL)
    return fromStorage ? `${fromStorage}` : `${this.collectionBaseUrl}`;
  }

  navigate(url: string, options: NavigationExtras={}){
    url = url.startsWith('/') ? url : `/${url}`;
    this.router.navigate([url], {...options});
  }

  navigateWithMerge(url: string, options: NavigationExtras={}) {
    this.navigate(url, { queryParamsHandling: 'merge', ...options });
  }

  navigateWithPreserve(url: string, options: NavigationExtras={}) {
    this.navigate(url, { queryParamsHandling: 'preserve', ...options });
  }

  navigateByURL(url: string) {
    this.router.navigateByUrl(url);
  }

  navigateAndReplace(url: string, options: NavigationExtras={}) {
    this.navigate(url, { queryParamsHandling: 'merge', replaceUrl: true, ...options });
  }

  navigateToExternalUrl(url?: string | URL, target?: string, features?: string){
    window.open(url, target, features);
  }

  getConvoCardsData(): Observable<{convos:any}> {
    const stateKey = makeStateKey<{convos:any}>('public-convo-cards');
    if (this.transferState.hasKey(stateKey)) {
      const cached = this.transferState.get(stateKey, { convos: [] });
      this.transferState.remove(stateKey);
      return of(cached);
    }
    return this.http.get<{convos:any}>(`${this.getAppBaseUrl()}/convos`).pipe(
      tap((response) => this.transferState.set(stateKey, response))
    );
  }

}
